import {createReducer, on} from "@ngrx/store";
import * as AccountActions from './account.actions';
import {initialAccountState} from "./account.state";
import {SchoolIntegrationStatus} from "@core/models/domain/account.domain";
import * as WondeSyncActions from "../wonde-sync/wonde-sync.actions";
import * as PupilLinkActions from "../pupil-link/pupil-link.actions";
import * as GroupActions from "../group/group.actions";
import {SchoolGroupStatus} from "@core/models/domain/group.domain";

export const accountReducer = createReducer(
    initialAccountState,

    // LOAD USERS
    on(AccountActions.loadUsers, state => ({ ...state, userLoading: true, userError: null })),
    on(AccountActions.loadUsersSuccess, (state, { users }) => ({
        ...state,
        userLoading: false,
        users,
        usersLoaded: true,
        // Date.now() keeps the action payloads unchanged; the only cost is that
        // devtools replay re-stamps the time, which the TTL check tolerates.
        usersLastLoadedAt: Date.now()
    })),
    on(AccountActions.loadUsersFailure, (state, { error }) => ({
        ...state,
        userLoading: false,
        userError: error
    })),

    // ADD USER
    on(AccountActions.addUser, state => ({ ...state, userLoading: true })),
    on(AccountActions.addUserSuccess, (state, { user }) => ({
        ...state,
        userLoading: false,
        userError: undefined,
        users: [...state.users, user]
    })),
    on(AccountActions.addUserFailure, (state, { error }) => ({
        ...state,
        userLoading: false,
        userError: error
    })),

    // MODIFY USER
    on(AccountActions.updateUser, state => ({ ...state, userLoading: true })),
    on(AccountActions.updateUserSuccess, (state, { user }) => {
        const updated = state.users.map(u => (u.userInfo.accountId === user.userInfo.accountId ? user : u));
        return { ...state, userLoading: false, userError: undefined, users: updated };
    }),
    on(AccountActions.updateUserFailure, (state, { error }) => ({
        ...state,
        userLoading: false,
        userError: error
    })),

    // remove
    on(AccountActions.removeUser, state => ({ ...state, userLoading: true })),
    on(AccountActions.removeUserSuccess, (state, { userAccountId }) => ({
        ...state,
        userLoading: false,
        users: state.users.filter(u => u.userInfo.accountId !== userAccountId)
    })),
    on(AccountActions.removeUserFailure, (state, { error }) => ({
        ...state,
        userLoading: false,
        userError: error
    })),

    // MODIFY USER PROFILE
    on(AccountActions.updateUserProfile, state => ({ ...state, userLoading: true })),
    on(AccountActions.updateUserProfileSuccess, (state, { userProfile }) => {
        const updated = state.users.map(u => {
            if (u.userInfo.accountId === userProfile.accountId) {
                let user = JSON.parse(JSON.stringify(u));
                user.userInfo.firstname = userProfile.firstName;
                user.userInfo.lastname = userProfile.lastName;
                user.userInfo.gender = userProfile.gender;
                user.userInfo.dateOfBirth = userProfile.dateOfBirth;
                user.userInfo.emailAddress = userProfile.emailAddress;
                user.userInfo.phoneNumber = userProfile.e164phoneNumber;
                user.userInfo.avatar = userProfile.avatar;
                return user;
            }
            return u;
        });
        return {
            ...state,
            userLoading: false,
            userError: undefined,
            users: updated
        };
    }),
    on(AccountActions.updateUserProfileFailure, (state, { error }) => ({
        ...state,
        userLoading: false,
        userError: error
    })),

      // LOAD SCHOOL INFO
    on(AccountActions.loadSchoolInfo, state => ({ ...state, schoolLoading: true, error: null })),
    on(AccountActions.loadSchoolInfoSuccess, (state, { schoolInfo }) => ({
        ...state,
        schoolLoading: false,
        schoolInfo
    })),
    on(AccountActions.loadSchoolInfoFailure, (state, { error }) => ({
        ...state,
        schoolLoading: false,
        schoolError: error
    })),

    // SYNC FROM INTEGRATION
    // The run lives on the backend, so the page never decides on its own that a sync is over: it
    // shows what the status says, which is the same whether the admin stayed or came back later.
    on(AccountActions.syncSchoolDataFromIntegration, state => ({
        ...state,
        integrationSchoolSyncLoading: true,
        integrationSchoolSyncError: null
    })),
    on(AccountActions.syncSchoolDataFromIntegrationStarted, state => ({
        ...state,
        integrationSchoolSyncLoading: true,
        integrationSchoolSyncError: null,
        // The figures on screen belong to the previous run; this one has not produced any yet.
        integrationSchoolSyncResult: null
    })),
    on(AccountActions.syncSchoolDataFromIntegrationFailure, (state, { error }) => ({
        ...state,
        integrationSchoolSyncLoading: false,
        integrationSchoolSyncError: error
    })),
    on(AccountActions.loadSyncStatusSuccess, (state, { status }) => {
        const justFinished = state.integrationSchoolSyncLoading && !status.running;
        const integrationStatus = (status.integrationStatus as SchoolIntegrationStatus) ?? state.schoolInfo?.integrationStatus;

        return {
            ...state,
            integrationSchoolSyncLoading: status.running,
            integrationSchoolSyncResult: status.result ?? null,
            integrationSchoolSyncError: status.error ?? null,
            integrationSyncStartedDatetime: status.startedDatetime ?? null,
            // A Wonde sync rewrites the roster server-side: invalidate the cache so the
            // next loadUsersIfNeeded fetches fresh data.
            usersLoaded: justFinished ? false : state.usersLoaded,
            schoolInfo: state.schoolInfo
                ? {
                    ...state.schoolInfo,
                    integrationStatus,
                    lastSyncDatetime: status.finishedDatetime ? new Date(status.finishedDatetime) : state.schoolInfo.lastSyncDatetime
                }
                : state.schoolInfo
        };
    }),
    // SYNC MODE
    on(AccountActions.setSyncMode, state => ({ ...state, syncModeSaving: true, syncModeError: null })),
    on(AccountActions.setSyncModeSuccess, (state, { syncMode }) => ({
        ...state,
        syncModeSaving: false,
        syncModeError: null,
        schoolInfo: state.schoolInfo ? { ...state.schoolInfo, syncMode } : state.schoolInfo
    })),
    on(AccountActions.setSyncModeFailure, (state, { error }) => ({
        ...state,
        syncModeSaving: false,
        syncModeError: error
    })),
    // DEACTIVATE SCHOOL
    on(AccountActions.deactivateSchool, state => ({ ...state, deactivatingSchool: true, deactivateSchoolError: null })),
    on(AccountActions.deactivateSchoolSuccess, state => ({ ...state, deactivatingSchool: false })),
    on(AccountActions.deactivateSchoolFailure, (state, { error }) => ({
        ...state,
        deactivatingSchool: false,
        deactivateSchoolError: error
    })),
    on(AccountActions.clearDeactivateSchoolError, state => ({ ...state, deactivateSchoolError: null })),
    on(AccountActions.loadSyncStatusFailure, (state, { error }) => ({
        ...state,
        integrationSchoolSyncError: error
    })),
    // Reviewing an MIS mismatch or a parent's pupil link request links or creates a pupil
    // server-side, so the list page must fetch again rather than show the cached roster.
    on(WondeSyncActions.linkMismatchSuccess, WondeSyncActions.createPupilSuccess,
        PupilLinkActions.resolveMismatchSuccess, PupilLinkActions.createPupilSuccess, state => ({
        ...state,
        usersLoaded: false
    })),

    // A deleted group is INACTIVE server-side; mirror it on the cached roster so pupils it leaves
    // without an active group are flagged at once instead of after the next reload (DIGI-1701).
    on(GroupActions.deleteGroupSuccess, (state, { groupId }) => ({
        ...state,
        users: state.users.map(user => user.schoolGroups?.some(group => group.id === groupId)
            ? {
                ...user,
                schoolGroups: user.schoolGroups.map(group => group.id === groupId
                    ? { ...group, status: SchoolGroupStatus.INACTIVE }
                    : group)
            }
            : user)
    })),

    // UPDATE SCHOOL (rename)
    on(AccountActions.updateSchool, state => ({ ...state, schoolLoading: true, schoolError: null })),
    on(AccountActions.updateSchoolSuccess, (state, { name }) => ({
        ...state,
        schoolLoading: false,
        schoolError: null,
        schoolInfo: { ...state.schoolInfo, name }
    })),
    on(AccountActions.updateSchoolFailure, (state, { error }) => ({
        ...state,
        schoolLoading: false,
        schoolError: error
    })),

    // UPDATE SCHOOL CONTACT (phone numbers + addresses)
    on(AccountActions.updateSchoolContact, state => ({ ...state, schoolLoading: true, schoolError: null })),
    on(AccountActions.updateSchoolContactSuccess, (state, { phoneNumbers, notificationRecipientIds, address }) => ({
        ...state,
        schoolLoading: false,
        schoolError: null,
        // A contact update that omits notificationRecipientIds did not touch them: keep the cached list.
        schoolInfo: { ...state.schoolInfo, phoneNumbers, address, notificationRecipientIds: notificationRecipientIds ?? state.schoolInfo?.notificationRecipientIds }
    })),
    on(AccountActions.updateSchoolContactFailure, (state, { error }) => ({
        ...state,
        schoolLoading: false,
        schoolError: error
    })),

    // UPDATE SCHOOL PHOTO
    on(AccountActions.updateSchoolLogo, state => ({ ...state, schoolLoading: true, schoolError: null })),
    on(AccountActions.updateSchoolLogoSuccess, (state, { logo }) => ({
        ...state,
        schoolLoading: false,
        schoolError: null,
        // An empty photo means it was removed, so the cached school carries no photo either.
        schoolInfo: { ...state.schoolInfo, logo: logo || undefined }
    })),
    on(AccountActions.updateSchoolLogoFailure, (state, { error }) => ({
        ...state,
        schoolLoading: false,
        schoolError: error
    })),
);